import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import {
  createSupportKbItem,
  updateSupportKbItem,
  fetchSupportCategories,
  fetchSupportTypes,
  getSupportKbItemById,
} from "../../Redux/Action/SupportKbAction";
import LoadingOverlay from "react-loading-overlay-ts";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

export default function SupportKbForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  // Main form data
  const [formData, setFormData] = useState({
    title: "",
    type: "document",
    category: "General",
    subcategory: "",
    audience: "public",
    visibility: "public",
    priority: 0,
    short_answer: "",
    question_variants_str: "",
    keywords_str: "",
    is_active: true,
  });

  // Keep ReactQuill content separate from the main form state
  const [content, setContent] = useState("");

  const { categories, selectedItem } = useSelector(
    (state) => state.supportKb
  );

  // --------------------------------------------------
  // Fetch categories/types and existing item
  // --------------------------------------------------
  useEffect(() => {
    dispatch(fetchSupportCategories());
    dispatch(fetchSupportTypes());

    if (id) {
      setLoading(true);

      dispatch(getSupportKbItemById(id))
        .unwrap()
        .catch(() => {
          toast.error("Failed to load support knowledge item");
          navigate("/support-knowledge-base");
        })
        .finally(() => setLoading(false));
    }
  }, [id, dispatch, navigate]);

  // --------------------------------------------------
  // Populate form when editing
  // --------------------------------------------------
  useEffect(() => {
    if (!id || !selectedItem || String(selectedItem._id) !== String(id)) {
      return;
    }

    // Populate normal fields
    setFormData({
      title: selectedItem.title || "",
      type: selectedItem.type || "document",
      category: selectedItem.category || "General",
      subcategory: selectedItem.subcategory || "",
      audience: selectedItem.audience || "public",
      visibility: selectedItem.visibility || "public",
      priority: selectedItem.priority ?? 0,
      short_answer: selectedItem.short_answer || "",

      question_variants_str: Array.isArray(
        selectedItem.question_variants
      )
        ? selectedItem.question_variants.join(", ")
        : "",

      keywords_str: Array.isArray(selectedItem.keywords)
        ? selectedItem.keywords.join(", ")
        : "",

      is_active:
        selectedItem.is_active !== undefined
          ? selectedItem.is_active
          : true,
    });

    // Populate ReactQuill separately
    setContent(selectedItem.content || "");
  }, [id, selectedItem]);

  // --------------------------------------------------
  // Handle normal input changes
  // --------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --------------------------------------------------
  // Handle submit
  // --------------------------------------------------
  const handleSubmit = (e) => {
    e.preventDefault();

    // Title validation
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    // Category validation
    if (!formData.category.trim()) {
      toast.error("Category is required");
      return;
    }

    // ReactQuill content validation
    const plainTextContent = content
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    if (!plainTextContent) {
      toast.error("Content cannot be empty");
      return;
    }

    // --------------------------------------------------
    // Prepare payload
    // --------------------------------------------------
    const payload = {
      title: formData.title.trim(),

      type: formData.type,

      category: formData.category.trim(),

      subcategory: formData.subcategory
        ? formData.subcategory.trim()
        : "",

      audience: formData.audience,

      visibility: formData.visibility,

      priority: Number(formData.priority) || 0,

      short_answer: formData.short_answer
        ? formData.short_answer.trim()
        : "",

      // ReactQuill content
      content: content,

      is_active: Boolean(formData.is_active),

      question_variants: formData.question_variants_str
        ? formData.question_variants_str
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],

      keywords: formData.keywords_str
        ? formData.keywords_str
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    // --------------------------------------------------
    // Create / Update
    // --------------------------------------------------
    setLoading(true);

    const action = id
      ? updateSupportKbItem({
          id,
          data: payload,
        })
      : createSupportKbItem(payload);

    dispatch(action)
      .unwrap()
      .then(() => {
        navigate("/support-knowledge-base");
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  // --------------------------------------------------
  // ReactQuill configuration
  // --------------------------------------------------
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["clean"],
    ],
  };

  // --------------------------------------------------
  // JSX
  // --------------------------------------------------
  return (
    <Layout>
      <LoadingOverlay
        active={loading}
        spinner
        text="Saving Support Knowledge Item..."
      >
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div
              className="modern-user-section-card shadow-sm border-0 p-4"
              style={{
                background: "#fff",
                borderRadius: 16,
              }}
            >
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                  <h4
                    className="mb-1"
                    style={{
                      color: "#034328",
                      fontWeight: 700,
                    }}
                  >
                    {id
                      ? "Edit Support Knowledge Item"
                      : "Create New Support Knowledge Item"}
                  </h4>

                  <p className="text-muted small mb-0">
                    Configure detailed attributes, audience targeting, and AI
                    context content.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 rounded-pill"
                  onClick={() =>
                    navigate("/support-knowledge-base")
                  }
                >
                  Cancel
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Title + Type */}
                <div className="row g-4 mb-4">
                  <div className="col-md-8">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Title *
                    </label>

                    <input
                      type="text"
                      name="title"
                      className="form-control form-control-lg border-gray-200"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g. How to initiate a refund request"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Type *
                    </label>

                    <select
                      name="type"
                      className="form-select form-select-lg border-gray-200"
                      value={formData.type}
                      onChange={handleChange}
                    >
                      <option value="document">
                        Document
                      </option>

                      <option value="faq">FAQ</option>

                      <option value="business_rule">
                        Business Rule
                      </option>

                      <option value="workflow">
                        Workflow
                      </option>

                      <option value="policy">Policy</option>

                      <option value="service">Service</option>

                      <option value="support">Support</option>

                      <option value="configuration">
                        Configuration
                      </option>
                    </select>
                  </div>
                </div>

                {/* Category + Subcategory + Priority */}
                <div className="row g-4 mb-4">
                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Category *
                    </label>

                    <input
                      type="text"
                      name="category"
                      className="form-control border-gray-200"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="e.g. Billing, Policies, Technical"
                      list="support-categories-list"
                      required
                    />

                    <datalist id="support-categories-list">
                      {categories.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Subcategory
                    </label>

                    <input
                      type="text"
                      name="subcategory"
                      className="form-control border-gray-200"
                      value={formData.subcategory}
                      onChange={handleChange}
                      placeholder="e.g. Refunds, Subscriptions"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Priority (Weight)
                    </label>

                    <input
                      type="number"
                      name="priority"
                      className="form-control border-gray-200"
                      value={formData.priority}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Audience + Visibility + Active */}
                <div className="row g-4 mb-4">
                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Target Audience
                    </label>

                    <select
                      name="audience"
                      className="form-select border-gray-200"
                      value={formData.audience}
                      onChange={handleChange}
                    >
                      <option value="public">
                        Public
                      </option>

                      <option value="care_seeker">
                        Care Seeker
                      </option>

                      <option value="carer">
                        Carer
                      </option>

                      <option value="partner">
                        Partner
                      </option>

                      <option value="agent">
                        Agent
                      </option>

                      <option value="admin">
                        Admin
                      </option>

                      <option value="internal">
                        Internal
                      </option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Visibility Level
                    </label>

                    <select
                      name="visibility"
                      className="form-select border-gray-200"
                      value={formData.visibility}
                      onChange={handleChange}
                    >
                      <option value="public">
                        Public
                      </option>

                      <option value="authenticated">
                        Authenticated
                      </option>

                      <option value="internal">
                        Internal
                      </option>
                    </select>
                  </div>

                  <div className="col-md-4 d-flex align-items-center pt-4">
                    <div className="form-check form-switch fs-5">
                      <input
                        className="form-check-input cursor-pointer"
                        type="checkbox"
                        id="support-is-active-switch"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                      />

                      <label
                        className="form-check-label fs-6 fw-bold ms-2"
                        htmlFor="support-is-active-switch"
                      >
                        Active for AI Retrieval
                      </label>
                    </div>
                  </div>
                </div>

                {/* Short Answer */}
                <div className="mb-4">
                  <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                    Short Answer / AI Snippet
                  </label>

                  <textarea
                    name="short_answer"
                    className="form-control border-gray-200"
                    rows={2}
                    value={formData.short_answer}
                    onChange={handleChange}
                    placeholder="Brief 1-2 sentence direct answer for instant AI chat extraction..."
                  />
                </div>

                {/* Question Variants + Keywords */}
                <div className="row g-4 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Question Variants (Comma-separated)
                    </label>

                    <input
                      type="text"
                      name="question_variants_str"
                      className="form-control border-gray-200"
                      value={formData.question_variants_str}
                      onChange={handleChange}
                      placeholder="e.g. How do I get my money back?, Can I refund?"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                      Keywords (Comma-separated)
                    </label>

                    <input
                      type="text"
                      name="keywords_str"
                      className="form-control border-gray-200"
                      value={formData.keywords_str}
                      onChange={handleChange}
                      placeholder="e.g. refund, billing, return, money"
                    />
                  </div>
                </div>

                {/* Full Content */}
                <div className="mb-5">
                  <label className="form-label fw-bold text-dark small uppercase tracking-wider">
                    Full Content *
                  </label>

                  <div
                    className="quill-wrapper shadow-sm rounded"
                    style={{
                      marginBottom: 20,
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={quillModules}
                      placeholder="Write or paste comprehensive documentation..."
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="d-flex justify-content-end gap-3 border-top pt-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() =>
                      navigate("/support-knowledge-base")
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="common-btn px-5 shadow"
                  >
                    {id
                      ? "Save Changes"
                      : "Create Knowledge Item"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}